import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
  DecisionTreeRunWithParentFieldsFragment, GetLiveSessionQuery, ListDecisionTreesQuery,
} from '../../../../generated/core-api/graphql-react-query';
import useSessionContext from '../../../LiveContext/SessionContextProvider/useSessionContext';
import useGlobalStoreContext from '../../GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';

function updateSessionRunInSession(session: GetLiveSessionQuery, value: DecisionTreeRunWithParentFieldsFragment, treeName?: string) {
  const runs = session.getSession.decisionTreeRuns ?? [];
  const existing = runs.find((run) => run.id === value.id);
  const patchedRun = {
    __typename: 'DecisionTreeRun' as const,
    id: value.id,
    decisionTreeId: value.decisionTreeId,
    createdAt: value.createdAt,
    finishedAt: value.finishedAt,
    parentId: value.parentId,
    decisionTree: existing?.decisionTree ?? (treeName ? { __typename: 'DecisionTree' as const, name: treeName } : null),
  };
  return {
    ...session,
    getSession: {
      ...session.getSession,
      decisionTreeRuns: existing
        ? runs.map((run) => (run.id === value.id ? patchedRun : run))
        : [...runs, patchedRun],
    },
  };
}

export default function useUpdateSessionDecisionTreeRun() {
  const queryClient = useQueryClient();
  const { sessionId } = useSessionContext();
  const { projectId: [projectId] } = useGlobalStoreContext();

  const updateSessionRun = React.useCallback((value: DecisionTreeRunWithParentFieldsFragment) => {
    if (value.parentId) {
      return;
    }

    const trees = queryClient.getQueryData<ListDecisionTreesQuery>(['listDecisionTrees', { input: { id: projectId ?? '' } }]);
    const treeName = trees?.getProject.decisionTrees.find((tree) => tree.id === value.decisionTreeId)?.name;

    queryClient.setQueryData<GetLiveSessionQuery>(['getLiveSession', { input: { id: sessionId ?? '' } }], (prev) => {
      if (!prev?.getSession) {
        return prev;
      }
      return updateSessionRunInSession(prev, value, treeName);
    });
  }, [projectId, queryClient, sessionId]);

  return updateSessionRun;
}
